import axios from 'axios';
import moment from 'moment';
import cookie from 'react-cookies';
import { cleanSlideMetric } from './Leaderboard';
import {
  AgentSlide,
  IPage,
  ISlideObservable,
  IUser,
  PageState,
  Slide,
  SlideObservable,
  SlidePeriod,
  SlideType,
} from './Slide';
import { MAX_AGENTS_PER_PAGE } from './slides/Agent';
import { getStartingDate } from '../../../recoil/atoms/startingDate';
import { getEndingDate } from '../../../recoil/atoms/endingDate';
import event from '../../../recoil/events/callReporting';

export interface ILeaderboardResponseData {
  businessId: number;
  createDate: string;
  createdBy: string;
  leaderboardId: number;
  name: string;
  slides: string;
}

export interface ILeaderboard {
  businessId: number;
  timeZone: string;
  createDate: string;
  createdBy: string;
  leaderboardId: number;
  name: string;
  slideObservables: ISlideObservable[];
  fetchedAt: Date;
}

export default async function useLeaderboard(
  businessId: number,
  apikey: string,
  timeZone: string,
  leaderboardId: string,
  getValues: (slide: AgentSlide) => Promise<any[] | undefined>
): Promise<ILeaderboard | undefined> {
  return new Promise((resolve, reject) => {
    const accessToken = cookie.load('accessToken');
    if (!businessId) return reject(new Error('missing businessId'));
    if (!leaderboardId) return reject(new Error('missing leaderboardId'));

    axios
      .post(
        `${process.env.VITE_APP_CDK_DOMAIN}/${process.env.VITE_APP_API_STAGE}/getLeaderboards`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: accessToken,
            'x-api-key': apikey,
          },
          businessid: businessId,
        }
      )
      .then(async (response) => {
        if (!response?.data?.data?.length) {
          return reject(new Error('missing response data'));
        }

        const leaderboardResponseData = (response.data
          ?.data as ILeaderboardResponseData[]).find(
          (leaderboard) =>
            leaderboard.leaderboardId.toString() === leaderboardId.toString()
        );

        if (!leaderboardResponseData) {
          return reject(
            new Error(`leaderboard not found for id ${leaderboardId}`)
          );
        }

        const leaderboard: ILeaderboard = {
          businessId: leaderboardResponseData.businessId,
          timeZone: timeZone,
          createDate: leaderboardResponseData.createDate,
          createdBy: leaderboardResponseData.createdBy,
          leaderboardId: leaderboardResponseData.leaderboardId,
          name: leaderboardResponseData.name,
          slideObservables: [],
          fetchedAt: new Date(),
        };

        const slides: Slide[] = await Promise.all(
          Array.from<Slide>(JSON.parse(leaderboardResponseData.slides))
            .filter((slide) => !!slide.active)
            .map((slide) => fetchSlidePages(slide, getValues))
        );

        leaderboard.slideObservables = slides.map(
          (slide) => new SlideObservable(slide)
        );

        resolve(leaderboard);
      })
      .catch((err) => {
        console.error(err); // eslint-disable-line no-console

        return reject(new Error(err));
      });
  });
}

const uuidv4 = () => {
  const u =
    Date.now().toString(16) + Math.random().toString(16) + '0'.repeat(16);
  return [
    u.substring(0, 8),
    u.substring(8, 4),
    '4000-8' + u.substring(13, 3),
    u.substring(16, 12),
  ].join('-');
};

const fetchSlidePages = async (
  slide: Slide,
  getValues: (slide: AgentSlide) => Promise<any[] | undefined>
): Promise<Slide> => {
  if (!slide.slideId) {
    slide.slideId = uuidv4();
  }

  switch (slide.type) {
    case SlideType.agent:
      return await fetchAgentSlidePages(slide, getValues);
    case SlideType.queue:
      return slide;
    default:
      throw new Error(`slide type ${(slide as any).type} not supported`);
  }
};

const fetchAgentSlidePages = async (
  slide: AgentSlide,
  getValues: (slide: AgentSlide) => Promise<any[] | undefined>
): Promise<Slide> => {
  slide.metric = cleanSlideMetric(slide.metric);

  const dateRangeParamsString = getAgentSlideDateRange(slide);
  window.sessionStorage.setItem('dateRange', dateRangeParamsString);

  const values = await getValues(slide);

  const users: IUser[] = (values ?? []).map((value, index) => ({
    rank: index + 1,
    icon: `${value.fname[0] ?? ''}${value.lname[0] ?? ''}`.toUpperCase(),
    name: `${value.fname} ${value.lname}`.trim(),
    avgduration: value.avgduration ?? '0:00:00',
    allcalls: value.allcalls ?? 0,
    allanswered: value.allanswered ?? 0,
    allconnections: value.allconnections ?? 0,
    alldurationavg: value.alldurationavg ?? 0,
    alldurationsum: value.alldurationsum ?? 0,
    allincoming: value.allincoming ?? 0,
    allmissed: value.allmissed ?? 0,
    alloutgoing: value.alloutgoing ?? 0,
    incominganswered: value.incominganswered ?? 0,
    incomingconnection: value.incomingconnection ?? 0,
    incomingmissed: value.incomingmissed ?? 0,
    outgoinganswered: value.outgoinganswered ?? 0,
    outgoingconnection: value.outgoingconnection ?? 0,
    outgoingmissed: value.outgoingmissed ?? 0,
    tduration: value.tduration ?? 0,
    data: value.data ?? [],
  }));

  slide.pages = paginate(users);

  return slide;
};

export const getAgentSlideDateRange = (slide: AgentSlide): string => {
  let start: Date;
  const end = moment().toDate();

  switch (slide.timePeriod) {
    case SlidePeriod.past14hours:
      start = moment().subtract(14, 'hours').toDate();
      break;
    case SlidePeriod.day:
      start = moment().subtract(1, 'day').toDate();
      break;
    case SlidePeriod.past24hours:
      start = moment().subtract(24, 'hours').toDate();
      break;
    case SlidePeriod.week:
      start = moment().subtract(1, 'week').toDate();
      break;
    case SlidePeriod.last7days:
      start = moment().subtract(7, 'days').toDate();
      break;
    case SlidePeriod.last30days:
      start = moment().subtract(30, 'days').toDate();
      break;
    case SlidePeriod.month:
      start = moment().subtract(1, 'month').toDate();
      break;
    case SlidePeriod.quarter:
      start = moment().subtract(3, 'months').toDate();
      break;
    default:
      throw new Error(`unsupported period: ${slide.timePeriod}`);
  }

  return `start=${start.toISOString()}&end=${end.toISOString()}`;
};

const paginate = (users: IUser[]): IPage[] => {
  const pages: IPage[] = [];
  for (let i = 0; i < users.length; i += MAX_AGENTS_PER_PAGE) {
    pages.push({
      state: PageState.offscreen,
      users: users.slice(i, i + MAX_AGENTS_PER_PAGE),
    });
  }
  return pages;
};

export const getbusinessuserreporting2 = async (
  userInfo,
  businessData,
  isNewDateRangeEnable
) => {
  if (
    !userInfo?.businessid ||
    !userInfo?.apikey ||
    !businessData?.connectionDuration ||
    !businessData?.timezone_php
  ) {
    return;
  }

  const yyyymmdd = (d) => d.toISOString().split('T')[0];

  const startDate = getStartingDate(isNewDateRangeEnable);
  const endDate = getEndingDate(isNewDateRangeEnable);
  event.startdate = isNewDateRangeEnable ? startDate : yyyymmdd(startDate);
  event.enddate = isNewDateRangeEnable ? endDate : yyyymmdd(endDate);
  (event as any).connectionLimit = businessData.connectionDuration;
  event.businessid = userInfo.businessid;
  event.apiKey = userInfo.apikey;
  event.timezone = businessData.timezone_php;
  const accessToken = cookie.load('accessToken');
  const result = await axios.post(
    `${process.env.VITE_APP_API_DOMAIN}/${process.env.VITE_APP_API_STAGE}/getbusinessuserreporting2`,
    JSON.stringify(event),
    {
      withCredentials: false,
      headers: {
        authorization: accessToken,
      },
    }
  );

  return result.data;
};
